import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { usePlayer } from "../contexts/PlayerContext";
import { LyricsModal } from "../components/LyricsModal";
import { LeadSheetViewer } from "../components/LeadSheetViewer";
import { MyLeadSheetzShelf } from "../components/MyLeadSheetzShelf";
import { QuizModal } from "../components/QuizModal";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "../components/ui/collapsible";
import { 
  Play, 
  Pause,
  Lock, 
  CheckCircle,
  ClipboardCheck,
  FileText, 
  Headphones, 
  Search,
  X,
  Volume2,
  Heart,
  CheckCircle2,
  Circle,
  Trophy,
  Crown,
  Zap,
  Rewind,
  FastForward,
  Repeat,
  Moon,
  Flame,
  Coffee,
  Target,
  Music,
  ChevronDown,
  ChevronRight,
  Clock,
  ListPlus,
  ListMusic,
  Coins,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import BuyCreditsModal from "../components/BuyCreditsModal";

const vibeIconMap = {
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
  Music
};

export default function LibraryPage() {
  const navigate = useNavigate();
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { playTrack, playingItem, isPlaying } = usePlayer();
  const [content, setContent] = useState([]);
  const [courses, setCourses] = useState([]);
  const [vibes, setVibes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || "all");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  // 10-second sample player state (separate from full playback)
  const previewAudioRef = useRef(null);
  const [previewingId, setPreviewingId] = useState(null);
  // Lyrics modal state — opens when a topic's "View Lyrics" button is clicked.
  const [lyricsModal, setLyricsModal] = useState(null); // { contentId, title }
  const [myLeadSheets, setMyLeadSheets] = useState([]);
  const [leadSheetOpen, setLeadSheetOpen] = useState(null);
  const unlockedSheetIds = new Set(myLeadSheets.map((s) => s.content_id));
  const [quizStatus, setQuizStatus] = useState({ published: [], listened: [], passed: [], can_take: false, skip_listen: false });
  const [quizOpen, setQuizOpen] = useState(null);
  const quizStatusRef = useRef(quizStatus);
  quizStatusRef.current = quizStatus;

  const loadLeadSheets = () => axios.get(`${API}/lead-sheets/mine`).then((r) => setMyLeadSheets(r.data.lead_sheets || [])).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/quizzes/status`).then((r) => setQuizStatus(r.data)).catch(() => {});
    if (!user) return;
    const onListened = (e) => {
      setQuizStatus((s) => (s.listened.includes(e.detail) ? s : { ...s, listened: [...s.listened, e.detail] }));
      if (quizStatusRef.current.published.includes(e.detail) && !quizStatusRef.current.listened.includes(e.detail)) {
        toast.success("Quiz unlocked! Test yourself on the song you just finished.");
      }
    };
    window.addEventListener("barsongz:listened", onListened);
    return () => window.removeEventListener("barsongz:listened", onListened);
  }, [user]);

  const openQuiz = (contentId) => {
    if (!quizStatus.signed_in) {
      toast.info("Sign in with a subscription to take song quizzes and unlock Lead Sheetz.", { action: { label: "Sign in", onClick: () => navigate("/login") } });
      return;
    }
    if (!quizStatus.can_take) {
      toast.info("Quizzes are included with a subscription.", { action: { label: "Subscribe", onClick: () => navigate("/subscribe") } });
      return;
    }
    if (!quizStatus.skip_listen && !quizStatus.listened.includes(contentId)) {
      toast.info("Listen to the whole song first. The quiz unlocks when it ends.");
      return;
    }
    setQuizOpen(contentId);
  };

  const onQuizPassed = (contentId) => {
    setQuizStatus((s) => ({ ...s, passed: [...new Set([...s.passed, contentId])] }));
    loadLeadSheets();
  };

  useEffect(() => {
    if (!user) { setMyLeadSheets([]); return; }
    loadLeadSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const togglePreview = (contentId) => {
    if (previewingId === contentId) {
      if (previewAudioRef.current) previewAudioRef.current.pause();
      setPreviewingId(null);
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    const a = new Audio(`${API}/packs/preview/${contentId}`);
    a.onended = () => setPreviewingId(null);
    a.onerror = () => {
      toast.error("Preview unavailable");
      setPreviewingId(null);
    };
    previewAudioRef.current = a;
    setPreviewingId(contentId);
    a.play().catch(() => setPreviewingId(null));
  };
  const [progress, setProgress] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [playlist, setPlaylist] = useState([]);
  const [progressStats, setProgressStats] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  // Pricing-at-a-glance state — fetched from /api/subscription/plans so the
  // strip stays in sync with founding-member status.
  const [subPricing, setSubPricing] = useState({ display: 9.99, foundingActive: false });

  useEffect(() => {
    // Independent fetch for pricing strip — runs once on mount
    (async () => {
      try {
        const r = await axios.get(`${API}/subscription/plans`);
        const founding = r.data?.founding_member_status?.available
          ? r.data?.plans?.find?.(p => p?.id === "founding_monthly")
          : null;
        const display = founding?.price_monthly || r.data?.plans?.find?.(p => p?.id === "unlimited_monthly")?.price_monthly || 9.99;
        setSubPricing({ display, foundingActive: !!founding });
      } catch (e) {
        // keep defaults
      }
    })();
  }, []);

  const categories = [
    { id: "all", name: "All Content" },
    { id: "1L", name: "1L" },
    { id: "2L", name: "2L" },
    { id: "3L", name: "3L" },
    { id: "bar_prep", name: "Bar Prep", comingSoon: true },
  ];

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (category) setSelectedCategory(category);
  }, [category]);

  const fetchData = async () => {
    try {
      const [contentRes, coursesRes, vibesRes] = await Promise.all([
        axios.get(`${API}/content`),
        axios.get(`${API}/courses-v2`),
        axios.get(`${API}/vibes`)
      ]);
      setContent(contentRes.data);
      setCourses(coursesRes.data);
      setVibes(vibesRes.data.vibes || []);
      
      if (user) {
        const statusRes = await axios.get(`${API}/subscription/status`);
        setSubscriptionStatus(statusRes.data);
        
        // Fetch credits for non-subscribers
        if (!statusRes.data?.has_subscription) {
          try {
            const creditsRes = await axios.get(`${API}/credits`);
            setUserCredits(creditsRes.data.audio_credits || 0);
          } catch (e) {
            setUserCredits(0);
          }
        }
        
        // Fetch playlist
        try {
          const playlistRes = await axios.get(`${API}/playlist`);
          setPlaylist(playlistRes.data.items || []);
        } catch (e) {}
        
        // Only fetch progress for premium users
        if (statusRes.data?.tier === "premium") {
          try {
            const [progressRes, favoritesRes, statsRes] = await Promise.all([
              axios.get(`${API}/progress`),
              axios.get(`${API}/content-favorites`),
              axios.get(`${API}/progress/stats`)
            ]);
            setProgress(progressRes.data);
            setFavorites(favoritesRes.data);
            setProgressStats(statsRes.data);
          } catch (e) {
            // Progress not available for basic tier
          }
        } else {
          // Still fetch favorites for basic tier
          try {
            const favoritesRes = await axios.get(`${API}/content-favorites`);
            setFavorites(favoritesRes.data);
          } catch (e) {}
        }
      }
    } catch (error) {
      console.error("Failed to fetch content", error);
    } finally {
      setLoading(false);
    }
  };

  const hasSubscription = subscriptionStatus?.has_subscription;
  const isPremium = subscriptionStatus?.tier === "premium";
  const usage = subscriptionStatus?.usage;

  const completedIds = progress.filter(p => p.completed).map(p => p.content_id);
  const favoriteIds = favorites.map(f => f.content_id);
  const playlistIds = playlist.map(p => p.content_id);

  // Filter content based on category and course - exclude PDFs (coming soon)
  const filteredContent = content.filter(item => {
    // Hide PDF content - it's coming soon
    if (item.content_type === "pdf") return false;
    
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchesCourse = !selectedCourse || item.course_name === selectedCourse;
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.course_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesCourse && matchesSearch;
  });

  // Group content by topic for topic-centric view
  const groupedByTopic = filteredContent.reduce((acc, item) => {
    // Create a unique topic key using title + course_name + category
    const topicKey = `${item.category}_${item.course_name}_${item.title}`.replace(/\s+/g, '_').toLowerCase();
    
    if (!acc[topicKey]) {
      acc[topicKey] = {
        topicKey,
        title: item.title,  // Use title for display
        topic: item.topic,  // Keep topic for internal use
        course_name: item.course_name,
        category: item.category,
        description: item.description,
        vibeVersions: [],
        hasPreview: false,
        isPlaceholder: true, // stays true only if EVERY version is a placeholder (no file_url)
      };
    }
    
    acc[topicKey].vibeVersions.push(item);
    if (item.is_preview) {
      acc[topicKey].hasPreview = true;
    }
    // A topic is a real (playable) topic if at least one item has real audio
    // attached. The backend strips file_url from responses for security and
    // exposes a `has_audio` boolean instead, so we check that. Placeholder
    // items (seeded before songs are uploaded) have has_audio=false and
    // is_placeholder=true.
    if (item.has_audio && !item.is_placeholder) {
      acc[topicKey].isPlaceholder = false;
    }
    if (item.has_lyrics) {
      acc[topicKey].hasLyrics = true;
      acc[topicKey].lyricsContentId = item.content_id;
    }
    if (item.has_lead_sheet) {
      acc[topicKey].leadSheetContentId = item.content_id;
    }
    
    return acc;
  }, {});

  // Sort topics alphabetically by title (default)
  const sortedTopics = Object.values(groupedByTopic).sort((a, b) => {
    return a.title.localeCompare(b.title);
  });

  const filteredCourses = courses.filter(course => 
    selectedCategory === "all" || course.category === selectedCategory
  );

  // Get vibe info helper
  const getVibeInfo = (vibeId) => vibes.find(v => v.id === vibeId);

  const toggleTopicExpanded = (topicKey) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicKey]: !prev[topicKey]
    }));
  };

  const handlePlay = async (item) => {
    // REQUIRE ACCOUNT FOR ALL PLAYBACK (including free previews)
    if (!user) {
      toast.info("Create an account to listen");
      navigate("/register", { state: { fromLibrary: true } });
      return;
    }

    // For locked content (non-preview), show buy credits modal for NON-SUBSCRIBERS
    if (item.locked && !item.is_preview && !hasSubscription) {
      try {
        const creditsRes = await axios.get(`${API}/credits`);
        setUserCredits(creditsRes.data.audio_credits || 0);
      } catch (e) {
        setUserCredits(0);
      }
      setShowBuyCreditsModal(true);
      return;
    }

    try {
      await playTrack(item);
      // Refresh usage after streaming starts (basic tier counts toward limit)
      if (user && !isPremium) {
        const statusRes = await axios.get(`${API}/subscription/status`);
        setSubscriptionStatus(statusRes.data);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429 || status === 403) {
        // Monthly limit reached or content locked — show buy credits modal
        try {
          const creditsRes = await axios.get(`${API}/credits`);
          setUserCredits(creditsRes.data.audio_credits || 0);
        } catch (e) {
          setUserCredits(0);
        }
        setShowBuyCreditsModal(true);
      }
      // Other errors already toasted by the player context
    }
  };

  const toggleComplete = async (contentId) => {
    if (!user) {
      toast.info("Please sign in to track progress");
      navigate("/login");
      return;
    }

    if (!isPremium) {
      toast.info("Upgrade to Premium to track your progress!");
      return;
    }

    const isCompleted = completedIds.includes(contentId);
    
    try {
      if (isCompleted) {
        await axios.delete(`${API}/progress/${contentId}`);
        setProgress(prev => prev.filter(p => p.content_id !== contentId));
        toast.success("Marked as incomplete");
      } else {
        await axios.post(`${API}/progress/update`, { content_id: contentId, completed: true });
        setProgress(prev => [...prev, { content_id: contentId, completed: true }]);
        toast.success("Marked as complete!");
      }
      const statsRes = await axios.get(`${API}/progress/stats`);
      setProgressStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.info("Upgrade to Premium to track your progress!");
      } else {
        toast.error("Failed to update progress");
      }
    }
  };

  const toggleFavorite = async (contentId) => {
    if (!user) {
      toast.info("Please sign in to add favorites");
      navigate("/login");
      return;
    }

    const isFavorite = favoriteIds.includes(contentId);
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/content-favorites/${contentId}`);
        setFavorites(prev => prev.filter(f => f.content_id !== contentId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/content-favorites/add`, { content_id: contentId });
        setFavorites(prev => [...prev, { content_id: contentId }]);
        toast.success("Added to favorites!");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const togglePlaylist = async (contentId) => {
    if (!user) {
      toast.info("Please sign in to add to playlist");
      navigate("/login");
      return;
    }

    const inPlaylist = playlistIds.includes(contentId);
    
    try {
      if (inPlaylist) {
        await axios.delete(`${API}/playlist/remove/${contentId}`);
        setPlaylist(prev => prev.filter(p => p.content_id !== contentId));
        toast.success("Removed from playlist");
      } else {
        const response = await axios.post(`${API}/playlist/add`, { content_id: contentId });
        if (response.data.success) {
          setPlaylist(prev => [...prev, { content_id: contentId }]);
          toast.success("Added to playlist!");
        }
      }
    } catch (error) {
      console.error("Playlist error:", error);
      toast.error(error.response?.data?.detail || "Failed to update playlist");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCourseProgress = (courseName) => {
    if (!progressStats?.by_course) return null;
    return progressStats.by_course.find(c => c.course_name === courseName);
  };

  // Check if any vibe version of a topic is completed
  const isTopicCompleted = (vibeVersions) => {
    return vibeVersions.some(v => completedIds.includes(v.content_id));
  };

  // Check if any vibe version of a topic is favorited
  const isTopicFavorited = (vibeVersions) => {
    return vibeVersions.some(v => favoriteIds.includes(v.content_id));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading library...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="library-page">
      <div className="container mx-auto px-4">
        {/* Header with Progress/Usage */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div>
              <h1 className="font-serif text-3xl font-bold mb-2">Content Library</h1>
              <p className="text-muted-foreground">
                {hasSubscription 
                  ? (isPremium ? "Premium access - unlimited streaming" : "Basic access - limited streaming")
                  : "Subscribe to unlock all content"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Browse topics and choose your preferred audio vibe
              </p>
            </div>
            
            {/* Usage/Progress Card */}
            {user && hasSubscription && (
              <Card className="bg-card/50 border-gold/30 min-w-[280px]">
                <CardContent className="py-4">
                  {isPremium && progressStats?.overall ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-gold" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Overall Progress</span>
                          <span className="text-sm text-gold font-bold">{progressStats.overall.percentage}%</span>
                        </div>
                        <Progress value={progressStats.overall.percentage} className="h-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {progressStats.overall.completed} of {progressStats.overall.total} completed
                        </p>
                      </div>
                    </div>
                  ) : usage ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Monthly Usage</span>
                        <Badge variant="outline" className="text-gold border-gold/50">Basic Plan</Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span>Audio: {usage.audio.used}/{usage.audio.limit}</span>
                          <span className="text-gold">{usage.audio.remaining} left</span>
                        </div>
                        <Progress value={(usage.audio.used / usage.audio.limit) * 100} className="h-1.5" />
                        <div className="flex items-center justify-between text-xs">
                          <span>PDFs: {usage.pdf.used}/{usage.pdf.limit}</span>
                          <span className="text-gold">{usage.pdf.remaining} left</span>
                        </div>
                        <Progress value={(usage.pdf.used / usage.pdf.limit) * 100} className="h-1.5" />
                      </div>
                      <Button 
                        size="sm" 
                        className="w-full bg-gold text-primary-foreground hover:bg-gold/90 text-xs"
                        onClick={() => navigate("/subscribe")}
                      >
                        <Crown className="h-3 w-3 mr-1" />
                        Upgrade to Premium
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search topics, courses..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                className={`${selectedCategory === cat.id ? "bg-gold text-primary-foreground" : ""} ${cat.comingSoon ? "opacity-70" : ""}`}
                onClick={() => { 
                  if (!cat.comingSoon) {
                    setSelectedCategory(cat.id); 
                    setSelectedCourse(null); 
                  }
                }}
                disabled={cat.comingSoon}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
                {cat.comingSoon && (
                  <Badge variant="outline" className="ml-2 text-xs border-gold/50 text-gold">
                    Coming Soon
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* PDF Flowcharts informational banner — kept at top because it carries
            the "PDFs coming soon" hint shown by the audio/PDF type filter chips.
            Subscription-pitch banner moved to the bottom of the page. */}
        {myLeadSheets.length > 0 ? (
          <MyLeadSheetzShelf sheets={myLeadSheets} onOpen={setLeadSheetOpen} />
        ) : (
        <Card className="mb-6 bg-gradient-to-r from-gold/10 to-gold/5 border-gold/30" data-testid="pdf-coming-soon-banner">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-gold" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-serif font-semibold">Legal Lead Sheetz™</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Unlock one-page visual study charts by passing song quizzes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Courses Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif">Courses</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1 pb-4">
                  <button
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors ${
                      !selectedCourse ? 'bg-accent text-gold' : ''
                    }`}
                    onClick={() => setSelectedCourse(null)}
                  >
                    All Courses
                  </button>
                  {filteredCourses.map(course => {
                    const courseProgress = getCourseProgress(course.course_name);
                    return (
                      <button
                        key={`${course.category}-${course.course_name}`}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors ${
                          selectedCourse === course.course_name ? 'bg-accent text-gold' : ''
                        }`}
                        onClick={() => setSelectedCourse(course.course_name)}
                        data-testid={`course-${course.course_name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{course.course_name}</span>
                          <div className="flex items-center gap-2">
                            {isPremium && courseProgress && courseProgress.percentage > 0 && (
                              <span className="text-xs text-gold">{courseProgress.percentage}%</span>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {course.topics_count}
                            </Badge>
                          </div>
                        </div>
                        {isPremium && courseProgress && courseProgress.percentage > 0 && (
                          <Progress value={courseProgress.percentage} className="h-1 mt-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Vibes Legend */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <Headphones className="h-4 w-4 text-gold" />
                  Audio Vibes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="text-xs text-muted-foreground mb-3">
                  Each topic has multiple audio versions with different energy levels
                </p>
                <div className="space-y-2">
                  {vibes.map(vibe => {
                    const VibeIcon = vibeIconMap[vibe.icon] || Music;
                    return (
                      <div key={vibe.id} className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${vibe.color}20` }}
                        >
                          <VibeIcon className="h-3 w-3" style={{ color: vibe.color }} />
                        </div>
                        <div>
                          <span className="text-sm font-medium">{vibe.name}</span>
                          <span className="text-xs text-muted-foreground ml-1">- {vibe.description}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Topics Grid - Topic-Centric View */}
          <div className="lg:col-span-3">
            {sortedTopics.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No content found matching your filters.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {sortedTopics.map(topicGroup => {
                  const isExpanded = expandedTopics[topicGroup.topicKey];
                  const topicCompleted = isTopicCompleted(topicGroup.vibeVersions);
                  const topicFavorited = isTopicFavorited(topicGroup.vibeVersions);
                  
                  return (
                    <Collapsible
                      key={topicGroup.topicKey}
                      open={!!isExpanded}
                      onOpenChange={() => topicGroup.isPlaceholder ? null : toggleTopicExpanded(topicGroup.topicKey)}
                    >
                      <Card 
                        className={`transition-all ${
                          topicGroup.isPlaceholder
                            ? 'opacity-60 grayscale border-dashed border-muted-foreground/30 cursor-not-allowed'
                            : topicCompleted ? 'border-green-500/30 bg-green-500/5' : 'hover:border-gold/50'
                        }`}
                        data-testid={`topic-card-${topicGroup.topicKey}`}
                      >
                        <CollapsibleTrigger asChild disabled={topicGroup.isPlaceholder}>
                          <CardContent
                            className="p-4 cursor-pointer"
                            onClick={(e) => {
                              // Radix Collapsible starts uncontrolled and self-toggles on
                              // click when `open` is undefined — the `disabled` prop on
                              // <CollapsibleTrigger asChild> is not enforced on a non-button
                              // child. Block the trigger at the CardContent level for
                              // placeholder topics so students never see an empty vibe list.
                              if (topicGroup.isPlaceholder) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                          >
                            <div className="flex items-center gap-4">
                              {/* Topic Icon */}
                              <div className="relative w-14 h-14 bg-card rounded-lg flex items-center justify-center flex-shrink-0 border border-border">
                                <Headphones className="h-6 w-6 text-gold" />
                                {topicCompleted && (
                                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="h-3 w-3 text-white" />
                                  </div>
                                )}
                              </div>

                              {/* Topic Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h3 className="font-serif font-semibold text-lg group-hover:text-gold transition-colors">
                                      {topicGroup.title}
                                      {topicGroup.isPlaceholder && (
                                        <span
                                          className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-muted text-muted-foreground border border-muted-foreground/20 align-middle"
                                          data-testid={`coming-soon-badge-${topicGroup.topicKey}`}
                                        >
                                          Coming soon
                                        </span>
                                      )}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">{topicGroup.course_name} • {topicGroup.category}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* 10-second preview - hidden for placeholder topics */}
                                    {!topicGroup.isPlaceholder && topicGroup.vibeVersions[0]?.content_id && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          togglePreview(topicGroup.vibeVersions[0].content_id);
                                        }}
                                        className="h-8 border-gold text-gold hover:bg-gold hover:text-primary-foreground"
                                        title="Listen to a 1-minute preview"
                                        data-testid={`topic-sample-btn-${topicGroup.topicKey}`}
                                      >
                                        {previewingId === topicGroup.vibeVersions[0].content_id ? (
                                          <><Pause className="h-3.5 w-3.5 mr-1" /> Stop</>
                                        ) : (
                                          <><Play className="h-3.5 w-3.5 mr-1" /> Preview</>
                                        )}
                                      </Button>
                                    )}
                                    {!topicGroup.isPlaceholder && topicGroup.hasLyrics && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setLyricsModal({
                                            contentId: topicGroup.lyricsContentId,
                                            title: topicGroup.title,
                                          });
                                        }}
                                        className="h-8 border-purple-500 text-purple-700 hover:bg-purple-500 hover:text-white"
                                        title="View lyrics"
                                        data-testid={`topic-lyrics-btn-${topicGroup.topicKey}`}
                                      >
                                        Lyrics
                                      </Button>
                                    )}
                                    {!topicGroup.isPlaceholder && topicGroup.leadSheetContentId && unlockedSheetIds.has(topicGroup.leadSheetContentId) && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); setLeadSheetOpen(topicGroup.leadSheetContentId); }}
                                        className="h-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                                        title="Open Lead Sheetz"
                                        aria-label={`Open Lead Sheetz for ${topicGroup.title}`}
                                        data-testid={`topic-lead-sheet-btn-${topicGroup.topicKey}`}
                                      >
                                        <FileText className="h-3.5 w-3.5 mr-1" /> Lead Sheet
                                      </Button>
                                    )}
                                    {!topicGroup.isPlaceholder && topicGroup.leadSheetContentId && !unlockedSheetIds.has(topicGroup.leadSheetContentId) && quizStatus.published.includes(topicGroup.leadSheetContentId) && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); toast.info(`Pass this song's quiz (${4} of 5) to unlock its Lead Sheet.`); }}
                                        className="h-8 border-dashed border-primary/50 text-primary/70"
                                        title="Pass the quiz to unlock"
                                        aria-label={`Lead Sheet for ${topicGroup.title} is locked. Pass the quiz to unlock it`}
                                        data-testid={`topic-lead-sheet-locked-${topicGroup.topicKey}`}
                                      >
                                        <Lock className="h-3.5 w-3.5 mr-1" /> Lead Sheet
                                      </Button>
                                    )}
                                    {(() => {
                                      if (topicGroup.isPlaceholder) return null;
                                      const quizItem = topicGroup.vibeVersions.find((v) => quizStatus.published.includes(v.content_id));
                                      if (!quizItem) return null;
                                      const passed = quizStatus.passed.includes(quizItem.content_id);
                                      const ready = quizStatus.can_take && (quizStatus.skip_listen || quizStatus.listened.includes(quizItem.content_id));
                                      return (
                                        <Button
                                          size="sm"
                                          variant={ready && !passed ? "default" : "outline"}
                                          onClick={(e) => { e.stopPropagation(); openQuiz(quizItem.content_id); }}
                                          className={`h-8 ${passed ? "border-green-500 text-green-700" : ready ? "" : "border-primary/40 text-primary/70"}`}
                                          title={passed ? "Quiz passed: take it again anytime" : ready ? "Take the quiz" : "Finish the song to unlock the quiz"}
                                          data-testid={`topic-quiz-btn-${topicGroup.topicKey}`}
                                        >
                                          {passed ? <CheckCircle className="h-3.5 w-3.5 mr-1" /> : ready ? <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                                          {passed ? "Passed" : "Quiz"}
                                        </Button>
                                      );
                                    })()}
                                    {/* Add to Playlist — topic-level shortcut */}
                                    {(() => {
                                      const versionsInPlaylist = topicGroup.vibeVersions.filter(v =>
                                        playlistIds.includes(v.content_id)
                                      ).length;
                                      const totalVersions = topicGroup.vibeVersions.length;
                                      const allInPlaylist = versionsInPlaylist === totalVersions;
                                      const someInPlaylist = versionsInPlaylist > 0;
                                      return (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (totalVersions === 1) {
                                              // Single vibe — add/remove directly
                                              togglePlaylist(topicGroup.vibeVersions[0].content_id);
                                            } else {
                                              // Multiple vibes — expand so user can pick
                                              toggleTopicExpanded(topicGroup.topicKey);
                                              if (!someInPlaylist) {
                                                toast.info("Pick a vibe below to add to your playlist");
                                              }
                                            }
                                          }}
                                          className={`h-8 ${
                                            someInPlaylist
                                              ? "border-primary bg-primary/10 text-primary hover:bg-primary/20"
                                              : "border-primary/40 text-primary hover:bg-primary/10"
                                          }`}
                                          title={
                                            totalVersions === 1
                                              ? (someInPlaylist ? "Remove from playlist" : "Add to playlist")
                                              : (allInPlaylist
                                                  ? "All vibes in playlist"
                                                  : someInPlaylist
                                                    ? `${versionsInPlaylist}/${totalVersions} vibes in playlist — expand to manage`
                                                    : "Pick a vibe and add to playlist")
                                          }
                                          data-testid={`topic-playlist-btn-${topicGroup.topicKey}`}
                                        >
                                          <ListPlus className={`h-3.5 w-3.5 ${someInPlaylist ? "fill-current" : ""}`} />
                                          <span className="ml-1 hidden sm:inline">
                                            {someInPlaylist ? "In Playlist" : "Playlist"}
                                          </span>
                                          {totalVersions > 1 && someInPlaylist && (
                                            <span className="ml-1 text-[10px] opacity-80">
                                              {versionsInPlaylist}/{totalVersions}
                                            </span>
                                          )}
                                        </Button>
                                      );
                                    })()}
                                    <Badge variant="outline" className="text-xs">
                                      {topicGroup.vibeVersions.length} {topicGroup.vibeVersions.length === 1 ? 'version' : 'versions'}
                                    </Badge>
                                  </div>
                                </div>
                                
                                <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                                  {topicGroup.description}
                                </p>

                                {/* Vibe Pills Preview */}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-muted-foreground">Available vibes:</span>
                                  <div className="flex gap-1">
                                    {topicGroup.vibeVersions.map(item => {
                                      const vibe = getVibeInfo(item.vibe);
                                      const VibeIcon = vibe ? vibeIconMap[vibe.icon] : Music;
                                      return (
                                        <div
                                          key={item.content_id}
                                          className="w-6 h-6 rounded-full flex items-center justify-center"
                                          style={{ backgroundColor: `${vibe?.color || '#c9a227'}20` }}
                                          title={vibe?.name || item.vibe}
                                        >
                                          {VibeIcon && <VibeIcon className="h-3 w-3" style={{ color: vibe?.color || '#c9a227' }} />}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              {/* Expand Icon */}
                              <div className="flex-shrink-0">
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-gold" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <div className="border-t border-border px-4 py-4 bg-muted/20">
                            <p className="text-sm font-medium mb-3 text-gold">Choose your vibe:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {topicGroup.vibeVersions.map(item => {
                                const vibe = getVibeInfo(item.vibe);
                                const VibeIcon = vibe ? vibeIconMap[vibe.icon] : Music;
                                const isCompleted = completedIds.includes(item.content_id);
                                const isFavorite = favoriteIds.includes(item.content_id);
                                const isPlaying = playingItem?.content_id === item.content_id;

                                return (
                                  <div
                                    key={item.content_id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                      isPlaying ? 'border-gold bg-gold/10' : 'border-border hover:border-gold/50'
                                    } ${isCompleted ? 'bg-green-500/5 border-green-500/30' : ''}`}
                                    data-testid={`vibe-item-${item.content_id}`}
                                  >
                                    {/* Vibe Icon */}
                                    <div 
                                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: `${vibe?.color || '#c9a227'}20` }}
                                    >
                                      {VibeIcon && <VibeIcon className="h-5 w-5" style={{ color: vibe?.color || '#c9a227' }} />}
                                    </div>

                                    {/* Vibe Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{vibe?.name || item.vibe}</span>
                                        {item.is_preview && (
                                          <Badge variant="outline" className="text-xs text-gold border-gold/50">Free</Badge>
                                        )}
                                        {isCompleted && (
                                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                                        )}
                                      </div>
                                      {item.song_title && (
                                        <p className="text-sm text-foreground font-medium truncate">{item.song_title}</p>
                                      )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className={`h-8 w-8 p-0 ${previewingId === item.content_id ? "text-gold" : "text-muted-foreground hover:text-gold"}`}
                                        onClick={(e) => { e.stopPropagation(); togglePreview(item.content_id); }}
                                        title="Listen to 10-second sample"
                                        data-testid={`sample-btn-${item.content_id}`}
                                      >
                                        {previewingId === item.content_id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                      </Button>

                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className={`h-8 w-8 p-0 ${isFavorite ? 'text-red-500' : 'text-muted-foreground'}`}
                                        onClick={(e) => { e.stopPropagation(); toggleFavorite(item.content_id); }}
                                        title="Add to favorites"
                                      >
                                        <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                                      </Button>

                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className={`h-8 w-8 p-0 ${playlistIds.includes(item.content_id) ? 'text-gold' : 'text-muted-foreground'}`}
                                        onClick={(e) => { e.stopPropagation(); togglePlaylist(item.content_id); }}
                                        title="Add to playlist"
                                      >
                                        <ListPlus className={`h-4 w-4 ${playlistIds.includes(item.content_id) ? 'fill-current' : ''}`} />
                                      </Button>
                                      
                                      {isPremium && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className={`h-8 w-8 p-0 ${isCompleted ? 'text-green-500' : 'text-muted-foreground'}`}
                                          onClick={(e) => { e.stopPropagation(); toggleComplete(item.content_id); }}
                                        >
                                          {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                                        </Button>
                                      )}

                                      <Button
                                        size="sm"
                                        variant={isPlaying ? "default" : "outline"}
                                        className={`${isPlaying ? "bg-gold text-primary-foreground" : ""} ${item.locked && !item.is_preview && !hasSubscription ? "border-gold/50 text-gold hover:bg-gold/10" : ""}`}
                                        onClick={(e) => { e.stopPropagation(); handlePlay(item); }}
                                        data-testid={`play-btn-${item.content_id}`}
                                      >
                                        {isPlaying ? (
                                          <>
                                            <Volume2 className="h-3 w-3 mr-1" />
                                            Playing
                                          </>
                                        ) : item.locked && !item.is_preview && !hasSubscription ? (
                                          <>
                                            <Lock className="h-3 w-3 mr-1" />
                                            Unlock
                                          </>
                                        ) : (
                                          <>
                                            <Play className="h-3 w-3 mr-1" />
                                            Play
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ─────────────── BOTTOM-OF-PAGE PROMO STRIP ─────────────── */}
        {/* "Unlock All Content" subscribe pitch — moved here from the top so
            it doesn't push the content grid down on first load. */}
        <Card
          className="mt-10 mb-6 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30"
          data-testid="growing-library-banner"
        >
          <CardContent className="py-5">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif font-semibold text-lg mb-1">
                    Unlock all content — our library is growing!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    New <span className="text-primary font-medium">2L</span> and{" "}
                    <span className="text-primary font-medium">3L courses</span>, plus{" "}
                    <span className="text-primary font-medium">PDF flowcharts</span>, are on the
                    way. Subscribe now to get instant access to every new track the moment it drops.
                  </p>
                </div>
              </div>
              {!hasSubscription && (
                <Button
                  onClick={() => navigate("/subscribe")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 self-start md:self-auto whitespace-nowrap"
                  data-testid="library-unlock-cta"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Subscribe Now
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing at a Glance — compact 3-card strip mirroring the landing page */}
        <div
          className="rounded-xl border border-border bg-secondary/30 px-4 sm:px-6 py-6 sm:py-8 mb-4"
          data-testid="library-pricing-strip"
        >
          <div className="text-center mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1.5">
              Pricing
            </p>
            <h2 className="font-serif text-xl md:text-2xl font-bold">At a glance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {/* Stream Unlimited */}
            <button
              onClick={() => navigate("/subscribe")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="library-price-card-subscription"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Subscription
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">Stream Unlimited</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">
                  ${subPricing.display}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  /month{subPricing.foundingActive ? " · founding" : ""}
                </p>
              </div>
            </button>

            {/* Buy Credits — pay-as-you-go streams */}
            <button
              onClick={() => navigate("/buy-credits")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="library-price-card-credits"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Pay-As-You-Go
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">20 Song Streams</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$10</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>

            {/* Pick Any 4 */}
            <button
              onClick={() => navigate("/packs")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="library-price-card-any4"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Download · Play Forever
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">Pick Any 4 Songs</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$25</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>

            {/* Course Pack — Best Value */}
            <button
              onClick={() => navigate("/packs")}
              className="relative group flex items-center justify-between gap-4 p-4 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all text-left"
              data-testid="library-price-card-course"
            >
              <span className="absolute -top-2 left-4 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                Best Value
              </span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Download · Play Forever
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">
                  Course Pack · 6 Songs
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$30</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>
          </div>
        </div>
      </div>



      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        open={showBuyCreditsModal} 
        onOpenChange={setShowBuyCreditsModal}
        userCredits={userCredits}
      />

      {/* Lyrics Modal */}
      <LyricsModal
        open={!!lyricsModal}
        onOpenChange={(o) => { if (!o) setLyricsModal(null); }}
        contentId={lyricsModal?.contentId}
        songTitle={lyricsModal?.title}
        token={token}
      />

      <LeadSheetViewer contentId={leadSheetOpen} onClose={() => setLeadSheetOpen(null)} />
      <QuizModal contentId={quizOpen} onClose={() => setQuizOpen(null)} onPassed={onQuizPassed} onOpenLeadSheet={setLeadSheetOpen} />
    </div>
  );
}
